import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserService } from '../user.service';
import { RoleService } from '../../roles/role.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-user-dialog',
  templateUrl: './user-dialog.component.html',
  styleUrls: ['./user-dialog.component.scss'],
})
export class UserDialogComponent implements OnInit {
  userForm: FormGroup;
  isEditMode = false;
  loading = false;
  roles: any[] = []; // ✅ fetched roles

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<UserDialogComponent>,
    private userService: UserService,
    private roleService: RoleService, // ✅ added
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.isEditMode = !!data;
    this.userForm = this.fb.group({
      fullName: [data?.fullName || '', Validators.required],
      email: [data?.email || '', [Validators.required, Validators.email]],
      password: ['', this.isEditMode ? [Validators.minLength(6)] : [Validators.required, Validators.minLength(6)]],
      role: [data?.role || data?.roles?.[0] || '', Validators.required],
    });
  }

  ngOnInit(): void {
    this.loadRoles();
  }

  /** ✅ Fetch roles dynamically from backend */
  loadRoles() {
    this.roleService.getRoles().subscribe({
      next: (res) => {
        this.roles = res;
      },
      error: (err) => {
        console.error('Failed to load roles:', err);
        Swal.fire('Error', 'Failed to load roles. Please try again.', 'error');
      },
    });
  }

  onSave() {
    if (this.userForm.invalid) return;

    const user = this.userForm.value;

    if (this.isEditMode) {
      Swal.fire({
        title: 'Confirm Update',
        text: 'Are you sure you want to update this user?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, update',
        cancelButtonText: 'Cancel',
      }).then((result) => {
        if (result.isConfirmed) {
          this.saveOrUpdateUser(user);
        }
      });
    } else {
      this.saveOrUpdateUser(user);
    }
  }

  private saveOrUpdateUser(user: any) {
    this.loading = true;

    const password = (user.password || '').trim();
    const role = user.role ? [user.role] : [];

    const payload = this.isEditMode
      ? {
          fullName: user.fullName,
          email: user.email,
          roles: role,
          ...(password ? { password } : {})
        }
      : {
          fullName: user.fullName,
          email: user.email,
          password,
          roles: role
        };

    const request$ = this.isEditMode
      ? this.userService.updateUser(this.data.id, payload)
      : this.userService.createUser(payload);

    request$.subscribe({
      next: (res: any) => {
        const status = res?.status ?? 200;
        this.loading = false;

        if (status >= 200 && status < 300) {
          Swal.fire({
            icon: 'success',
            title: this.isEditMode ? 'User Updated' : 'User Created',
            text:
              typeof res?.body === 'string' && res.body.length
                ? res.body
                : this.isEditMode
                ? 'Updated successfully'
                : 'Created successfully',
            confirmButtonText: 'OK',
          }).then(() => {
            this.dialogRef.close(true);
          });
        } else {
          Swal.fire('Error', 'Unexpected response from server.', 'error');
        }
      },
      error: (err) => {
        this.loading = false;

        // Defensive success catch
        if (err && (err.status >= 200 && err.status < 300)) {
          Swal.fire({
            icon: 'success',
            title: this.isEditMode ? 'User Updated' : 'User Created',
            confirmButtonText: 'OK',
          }).then(() => this.dialogRef.close(true));
          return;
        }

        console.error('Save error', err);
        Swal.fire(
          'Error',
          err?.error || err?.message || 'Something went wrong. Please try again.',
          'error'
        );
      },
    });
  }

  onCancel() {
    this.dialogRef.close();
  }
}
